import { useTranslation } from "react-i18next";
import { Button, cn, Icon, type IconName } from "@nexus/ui";
import type {
  ChangelogChannel,
  ChangelogDelivery,
  ChangelogDeliveryStatus,
} from "@/features/changelog/api/changelog.api";
import {
  CHANGELOG_CHANNEL_ORDER,
  formatDeployedAt,
} from "@/features/changelog/lib/changelog-display";

export const CHANNEL_ICON: Record<ChangelogChannel, IconName> = {
  IN_APP: "volume-2",
  SLACK: "message-circle",
  EMAIL: "mail",
};

const STATUS_ICON: Record<ChangelogDeliveryStatus, IconName> = {
  PENDING: "clock",
  SENDING: "loader",
  SENT: "check-circle",
  FAILED: "x-circle",
};

const STATUS_COLOR: Record<ChangelogDeliveryStatus, string> = {
  PENDING: "--muted-foreground",
  SENDING: "--muted-foreground",
  SENT: "--success",
  FAILED: "--error",
};

interface ChangelogDeliveryListProps {
  deliveries: ChangelogDelivery[];
  canRetry: boolean;
  isBusy: boolean;
  onRetry: (channel: ChangelogChannel, force: boolean) => void;
}

/**
 * One row per channel that was asked for. A channel nobody picked is absent
 * rather than shown as "not sent" — the card already says the notes went out,
 * and listing every road not taken buries the one that failed.
 */
export function ChangelogDeliveryList({
  deliveries,
  canRetry,
  isBusy,
  onRetry,
}: ChangelogDeliveryListProps) {
  const { t, i18n } = useTranslation();

  const ordered = CHANGELOG_CHANNEL_ORDER.map((channel) =>
    deliveries.find((delivery) => delivery.channel === channel),
  ).filter((delivery): delivery is ChangelogDelivery => delivery !== undefined);

  if (ordered.length === 0) return null;

  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {ordered.map((delivery) => (
        <li key={delivery.channel} className="flex items-start gap-2.5 text-xs">
          <Icon
            name={CHANNEL_ICON[delivery.channel]}
            size="xs"
            color="muted"
            className="mt-0.5"
            aria-hidden
          />
          <span className="w-16 shrink-0 text-muted-foreground">
            {t(`admin.changelog.channels.${delivery.channel}`, { defaultValue: delivery.channel })}
          </span>
          <Icon
            name={delivery.isStuck ? "alert-circle" : STATUS_ICON[delivery.status]}
            size="xs"
            colorVar={delivery.isStuck ? "--warning" : STATUS_COLOR[delivery.status]}
            className={cn(
              "mt-0.5",
              delivery.status === "SENDING" && !delivery.isStuck && "animate-spin",
            )}
            aria-hidden
          />
          <span className="flex-1 text-foreground">
            <DeliverySummary delivery={delivery} locale={i18n.language} />
          </span>
          {canRetry && (delivery.status === "FAILED" || delivery.isStuck) ? (
            <Button
              variant="outline"
              size="sm"
              disabled={isBusy}
              onClick={() => onRetry(delivery.channel, delivery.isStuck)}
            >
              {delivery.isStuck
                ? t("admin.changelog.delivery.forceRetry", "Force retry")
                : t("admin.changelog.delivery.retry", "Retry")}
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function DeliverySummary({
  delivery,
  locale,
}: {
  delivery: ChangelogDelivery;
  locale: string;
}) {
  const { t } = useTranslation();

  if (delivery.isStuck) {
    return (
      <span className="text-warning-subtle-foreground">
        {t(
          "admin.changelog.delivery.stuck",
          "Started sending but never finished. Forcing a retry may deliver twice.",
        )}
      </span>
    );
  }
  if (delivery.status === "PENDING") {
    return <>{t("admin.changelog.delivery.queued", "Queued")}</>;
  }
  if (delivery.status === "SENDING") {
    return <>{t("admin.changelog.delivery.sending", "Sending…")}</>;
  }
  if (delivery.status === "FAILED") {
    return (
      <span className="text-error">
        {delivery.note ?? t("admin.changelog.delivery.failed", "Delivery failed")}
      </span>
    );
  }

  const sentAt = delivery.sentAt ? formatDeployedAt(delivery.sentAt, locale) : null;
  const by = delivery.requestedByName;

  return (
    <>
      {delivery.recipientCount === null
        ? by
          ? t("admin.changelog.delivery.sentByAt", "Sent {{at}} by {{by}}", {
              at: sentAt ?? "",
              by,
            })
          : t("admin.changelog.delivery.sent", "Sent {{at}}", { at: sentAt ?? "" })
        : by
          ? t("admin.changelog.delivery.sentToCountBy", {
              count: delivery.recipientCount,
              at: sentAt ?? "",
              by,
            })
          : t("admin.changelog.delivery.sentToCount", {
              count: delivery.recipientCount,
              at: sentAt ?? "",
            })}
      {/* A SENT row can still carry a note: an email send that reached most of
          the list is a success worth recording, not a failure worth retrying. */}
      {delivery.note ? (
        <span className="block text-[11px] text-warning-subtle-foreground">{delivery.note}</span>
      ) : null}
    </>
  );
}
